import { useCallback, useEffect, useState } from "react";
import { emitter } from "../../shared";
import modalRegistry from "./modal-registry";

type EventPayload = Record<string, unknown>;

type ModalQueue = {
  eventType: string;
  payload: EventPayload;
}[];

export function ModalManager() {
  const [modalQueue, setModalQueue] = useState<ModalQueue>([]);
  const [activeModal, setActiveModal] = useState<{
    eventType: string;
    payload: EventPayload;
  } | null>(null);

  const addModalToQueue = useCallback((eventType: string, payload: EventPayload) => {
    if (modalRegistry.get(eventType)) {
      setModalQueue((prevQueue) => [...prevQueue, { eventType, payload }]);
    }
  }, []);

  const closeModal = () => setActiveModal(null);

  useEffect(() => {
    if (!activeModal && modalQueue.length > 0) {
      setActiveModal(modalQueue[0]);
      setModalQueue((prevQueue) => prevQueue.slice(1));
    }
  }, [activeModal, modalQueue]);

  useEffect(() => {
    const unbinds = [...modalRegistry.keys()].map((eventType) => {
      return emitter.on(eventType as any, (payload: EventPayload) => {
        addModalToQueue(eventType, payload);
      });
    });

    return () => unbinds.forEach((unbind) => unbind());
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    window.addEventListener(
      "close",
      ({ target }) => {
        if ((target as HTMLDialogElement)?.tagName === "DIALOG") {
          closeModal();
        }
      },
      { signal: controller.signal, capture: true },
    );

    return () => controller.abort();
  }, []);

  const ActiveModalComponent = activeModal
    ? modalRegistry.get(activeModal.eventType)
    : null;
  const modalPayload = activeModal?.payload ?? {};

  return ActiveModalComponent && <ActiveModalComponent payload={modalPayload} />;
}
